import type { ReactNode } from "react";
import type { MDXRemoteSerializeResult } from "next-mdx-remote";
import CustomMDXRemote from "@components/mdx/CustomMDXRemote";
import { SectionsData } from "@lib/frontend/sections-data";
import * as gtag from "@lib/utilities/analytics";

/**
 * Shared pieces for section implementations, so twenty sections do not each
 * invent their own heading scale and MDX wrapper.
 */

/** Shorthand over the shared CustomMDXRemote (the S&B-reused renderer):
 *  prose rhythm + link behaviours (next/link internally, hardened
 *  externally, consent-button hrefs). */
export const Prose = ({ content, className = "" }: { content?: MDXRemoteSerializeResult; className?: string }) =>
  <CustomMDXRemote content={content} className={className} />;

/** Section heading + optional intro, in the approved scale. Left-aligned
 *  always (artifact). `icon` renders before the text in accent violet — the
 *  artifact's m-h pattern; pass one only where it carries meaning. */
export const SectionHeading = ({
  heading,
  intro,
  introText,
  icon,
}: {
  heading?: string;
  intro?: MDXRemoteSerializeResult;
  /** plain-string lede for sections whose intro is not CMS richtext (the map
   *  section's invite) — rendered in the serif lede register */
  introText?: string;
  icon?: ReactNode;
}) =>
  heading || intro || introText ? (
    <header className="mb-8 text-left">
      {heading ? (
        <h2 className="flex items-center gap-2.5 text-2xl font-extrabold tracking-tight text-ink md:text-3xl">
          {icon ? <span className="flex-none text-violet-700 [&>svg]:h-[0.85em] [&>svg]:w-[0.85em]" aria-hidden>{icon}</span> : null}
          {heading}
        </h2>
      ) : null}
      {intro ? <Prose content={intro} className="mt-3 text-muted" /> : null}
      {!intro && introText ? <p className="mt-3 max-w-2xl font-serif text-muted">{introText}</p> : null}
    </header>
  ) : null;

/** sectionsData off the page prop — every list section reads through this. */
export const dataOf = (page: unknown): SectionsData =>
  ((page as { sectionsData?: SectionsData } | undefined)?.sectionsData ?? {});

/** The three blob variants, cycled so a row of masks is not one repeated shape. */
export const blobFor = (index: number) =>
  ["blob-1", "blob-2", "blob-3", "blob-4", "blob-5", "blob-6"][index % 6];

/**
 * Opens the appointment dialog from anywhere. Layout owns the dialog and
 * listens for this event; a section deep in a dynamic zone should not need a
 * prop drilled through the zone dispatcher to ask for it.
 */
export const OPEN_APPOINTMENT_EVENT = "physiowave:open-appointment";
/** same contract for the search overlay (Layout owns it, listens for this) */
export const OPEN_SEARCH_EVENT = "physiowave:open-search";
export const openSearchOverlay = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(OPEN_SEARCH_EVENT));
  }
};
/** `source` labels the funnel entry in GA (sidebar, hero, cta-section, …).
 *  It also rides the event detail: Layout hands it to the modal so the
 *  eventual appointment_submit carries `modal:<source>` — open AND submit
 *  are then attributable to the same θέση (owner, P11-d). */
export const openAppointmentModal = (source?: string) => {
  if (typeof window !== "undefined") {
    const label = typeof source === "string" ? source : "unknown";
    gtag.event("appointment_open", {
      event_category: "conversion",
      event_label: label,
    });
    window.dispatchEvent(new CustomEvent(OPEN_APPOINTMENT_EVENT, { detail: { source: label } }));
  }
};
